import React from 'react';

/*
 * Formatting for the newsletter editor, for people who do not write HTML.
 *
 * Built on the browser's own editing commands. They are old and marked
 * deprecated, but every browser still ships them, they act on exactly the
 * selection the person made, and they put the change on the browser's undo
 * stack — none of which a hand-rolled Range rewrite gets right as cheaply.
 *
 * Two rules keep it working:
 *
 * - Every control acts on mousedown and cancels it. A click would move focus
 *   to the button, which ends the selection and blurs the editor — and the
 *   editor saves on blur. Nothing here is a <select> for the same reason.
 *
 * - styleWithCSS is switched on before every command, so colour and size
 *   come out as <span style="…">, which the newsletter cleaner keeps and every
 *   mail client reads. Left off, the browser writes <font color>, which the
 *   cleaner strips.
 */

const SIZES = [
  // execCommand's 1–7 scale, which with styleWithCSS becomes a CSS keyword.
  { value: '2', label: 'Small', css: 'small' },
  { value: '3', label: 'Normal', css: 'medium' },
  { value: '5', label: 'Large', css: 'x-large' },
  { value: '6', label: 'Huge', css: 'xx-large' },
];

function NewsletterFormatBar({ editorRef, colours, onChange }) {
  const run = (command, value) => {
    const el = editorRef.current;
    if (!el) return;
    // A button pressed with the caret outside the letter acts on the letter.
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || !el.contains(sel.anchorNode)) el.focus();
    document.execCommand('styleWithCSS', false, true);
    document.execCommand(command, false, value);
    onChange();
  };

  const addLink = () => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) {
      window.alert('Select the words that should be the link first.');
      return;
    }
    // Held across the prompt, which can drop the selection in some browsers.
    const range = sel.getRangeAt(0).cloneRange();
    const url = window.prompt('Link to (a web address):', 'https://');
    if (!url || url === 'https://') return;
    sel.removeAllRanges();
    sel.addRange(range);
    run('createLink', /^(https?:|mailto:)/i.test(url.trim()) ? url.trim() : 'https://' + url.trim());
  };

  // mousedown, not click — see the note at the top.
  const act = fn => ({ onMouseDown: e => { e.preventDefault(); fn(); } });

  const Btn = ({ title, icon, children, onAct }) => (
    <button type="button" className="newsletter-fmt-btn" title={title} aria-label={title} {...act(onAct)}>
      {icon ? <i className={`fa ${icon}`}></i> : children}
    </button>
  );

  const swatches = [
    { name: 'Club colour', hex: colours.c60 },
    { name: 'Club second colour', hex: colours.c30 },
    { name: 'Club accent', hex: colours.c10 },
    { name: 'Dark text', hex: '#1f2d3d' },
    { name: 'Grey', hex: '#6b7280' },
    { name: 'Red', hex: '#c62828' },
    { name: 'Blue', hex: '#1565c0' },
  ].filter(s => s.hex);

  return (
    <div className="newsletter-fmt" role="toolbar" aria-label="Formatting">
      <div className="newsletter-fmt-group">
        <Btn title="Undo" icon="fa-undo" onAct={() => run('undo')} />
        <Btn title="Redo" icon="fa-repeat" onAct={() => run('redo')} />
      </div>
      <div className="newsletter-fmt-group">
        <Btn title="Bold" icon="fa-bold" onAct={() => run('bold')} />
        <Btn title="Italic" icon="fa-italic" onAct={() => run('italic')} />
        <Btn title="Underline" icon="fa-underline" onAct={() => run('underline')} />
      </div>
      <div className="newsletter-fmt-group" aria-label="Text size">
        {SIZES.map(s => (
          <Btn key={s.value} title={`Text size: ${s.label}`} onAct={() => run('fontSize', s.value)}>
            <span style={{ fontSize: s.css === 'xx-large' ? '1.25em' : s.css === 'x-large' ? '1.1em' : s.css === 'small' ? '0.85em' : '1em' }}>
              {s.label}
            </span>
          </Btn>
        ))}
      </div>
      <div className="newsletter-fmt-group" aria-label="Paragraph style">
        <Btn title="Heading" onAct={() => run('formatBlock', 'h2')}><strong>Heading</strong></Btn>
        <Btn title="Subheading" onAct={() => run('formatBlock', 'h3')}>Subheading</Btn>
        <Btn title="Plain paragraph" onAct={() => run('formatBlock', 'p')}>Paragraph</Btn>
      </div>
      <div className="newsletter-fmt-group" aria-label="Text colour">
        {swatches.map(s => (
          <button key={s.name} type="button" className="newsletter-fmt-swatch"
            title={`Text colour: ${s.name}`} aria-label={`Text colour: ${s.name}`}
            style={{ background: s.hex }} {...act(() => run('foreColor', s.hex))} />
        ))}
      </div>
      <div className="newsletter-fmt-group">
        <Btn title="Align left" icon="fa-align-left" onAct={() => run('justifyLeft')} />
        <Btn title="Centre" icon="fa-align-center" onAct={() => run('justifyCenter')} />
        <Btn title="Bulleted list" icon="fa-list-ul" onAct={() => run('insertUnorderedList')} />
        <Btn title="Numbered list" icon="fa-list-ol" onAct={() => run('insertOrderedList')} />
        <Btn title="Make the selected words a link" icon="fa-link" onAct={addLink} />
        <Btn title="Clear formatting from the selection" icon="fa-eraser" onAct={() => run('removeFormat')} />
      </div>
    </div>
  );
}

export default NewsletterFormatBar;
